<template>
  <div class="modal-container">
    <!-- Overlay closes modal -->
    <div class="modal-overlay" @click="closeModal">
      <!-- Content stops propagation -->
      <div class="modal-content" :class="modalSizeClass" @click.stop>
        <div class="modal-header">
          <!-- ✅ Check for slot -->
          <slot name="header">
            <!-- fallback header -->
            <span><b>{{ modalHeading }}</b></span>
          </slot>
          <button class="modal-close-button" @click="closeModal">X</button>
        </div>

        <div class="modal-body">
          <div v-html="modalContent"></div>
          <div v-if="isLoading" class="loader">Loading...</div>
          <iframe
            v-if="iframeUrl"
            :src="iframeUrl"
            @load="onIframeLoad"
            :class="iframeHeightClass"
            style="width: 100%;"
            title="description"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    modalHeading: {
      type: String,
      default: ""
    },
    modalContent: {
      type: String,
      default: ""
    },
    iframeUrl: {
      type: String,
      default: ""
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg", "xl", "2xl"].includes(value)
    },
    refreshOnClose: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    modalSizeClass() {
      // If it's a quote modal (has content but no iframe), use smaller size
      if (this.modalContent && !this.iframeUrl && this.size === 'md') {
        return 'modal-quote';
      }
      return `modal-${this.size}`;
    },
    iframeHeightClass() {
      const heightMap = {
        sm: "iframe-sm",
        md: "iframe-md",
        lg: "iframe-lg",
        xl: "iframe-xl"
      };
      return heightMap[this.size] || "iframe-md";
    }
  },
  methods: {
    closeModal() {
      this.$emit("close");
      // Removed page refresh on modal close - modals should not refresh the page
    },
    onIframeLoad() {
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.modal-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
    /* higher for visibility */
}

.modal-overlay {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
}

.modal-content {
    background-color: #fff;
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
    /* Ensure modal stays within viewport */
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Quote modal - smaller size, not 90vh */
.modal-quote {
    width: 600px;
    max-width: 90vw;
    min-height: auto;
    height: auto;
}

/* Regular modals - 90vw x 90vh */
.modal-sm,
.modal-md,
.modal-lg,
.modal-xl {
    min-width: 90vw;
    min-height: 90vh;
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
}

.modal-2xl {
    min-width: 96vw;
    min-height: 96vh;
    max-width: calc(100vw - 10px);
    max-height: calc(100vh - 10px);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
}

.modal-body {
    width: 100%;
    margin-top: 8px;
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
}

.modal-quote .modal-body {
    overflow-y: visible;
}

.modal-close-button {
    border: 0;
    outline: none;
    background: transparent;
    font-size: 16px;
    cursor: pointer;
}

.loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 18px;
    font-weight: bold;
}

.modal-body iframe {
    border: none;
    width: 100%;
    display: block;
}

.iframe-sm {
    height: calc(90vh - 120px);
    max-height: 100%;
}

.iframe-md {
    height: calc(90vh - 120px);
    max-height: 100%;
}

.iframe-lg {
    height: calc(90vh - 120px);
    max-height: 100%;
}

.iframe-xl {
    height: calc(90vh - 120px);
    max-height: 100%;
}

.iframe-2xl {
    height: calc(96vh - 120px);
    max-height: 100%;
}
</style>
